"use strict"

import React, { useState, useEffect } from "react"
import { Button, Dropdown, DropdownButton, Modal } from "react-bootstrap"

import { logToServer } from "../../../ErrorBoundary"
import { getObjectsList, updateObject, addNewObject } from "../../../Loaders"
import DashLoader from "../../widgets/DashLoader"
import { getFormattedMessage } from "../../../components/shared_components_registration"

export default function EditSingleFileModal({ file_id, show, onHide, tableReload, intl, singleFileType }) {
  const getText = (id, msg) => getFormattedMessage(id, msg, true, intl)
  const [fileInfo, setFileInfo] = useState(null)
  const [filetype, setFiletype] = useState(null)
  const [reqMetaData, setReqMetaData] = useState({})
  const [extraMetaData, setExtraMetaData] = useState({})
  const [file, setFile] = useState(null)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [fileTypeOptions, setFileTypeOptions] = useState([])
  const [error, setError] = useState(false)
  const [fileErr, setFileErr] = useState(null)
  const [filetypeErr, setFiletypeErr] = useState(null)
  const [fileSubmitErr, setFileSubmitErr] = useState(null)
  const [creatingFile, setCreatingFile] = useState(false)
  const logError = (msg, setErr) => {
    setErr(msg)
    setError(true)
    console.error(msg)
    logToServer(msg)
  }

  const getFileTypeInfo = (ft, currentMetadata) => {
    const req_fields = ft?.metadata_spec?.required || []
    const metadata = {}
    req_fields.forEach((field) => (metadata[field] = currentMetadata?.[field] || ""))

    setFiletype(ft)
    setReqMetaData(metadata)
  }

  useEffect(() => {
    setCreatingFile(!file_id)
    file_id
      ? getObjectsList(`/lake/files/${file_id}`)
          .then((data) => {
            setExtraMetaData(data.metadata)
            getFileTypeInfo(data?.filetype, data.metadata)
            setName(data?.name)
            setDescription(data?.description)
            setFileInfo(data)
          })
          .catch((err) => {
            logError(err, setFileErr)
          })
      : setFileInfo({})

    getObjectsList(`/lake/filetypes/`)
      .then((data) => {
        setFileTypeOptions(data)
      })
      .catch((err) => {
        logError(err, setFiletypeErr)
      })
  }, [])

  useEffect(() => {
    const ft = fileTypeOptions.filter((ft) => {
      return ft.name === singleFileType
    })
    setFileSubmitErr(null)
    getFileTypeInfo(ft[0], extraMetaData)
  }, [singleFileType, fileTypeOptions])

  const submitFileEdit = (e) => {
    e.preventDefault()
    if (
      !name ||
      !description ||
      !filetype?.name ||
      Object.values(reqMetaData).filter((val) => !val).length > 0 ||
      (!file && creatingFile)
    ) {
      alert(getText("general.required.form.fields.empty", "Please fill out all required form fields"))
      return
    }

    let formData = new FormData()
    file && formData.append("file", file)
    fileInfo.name !== name && formData.append("name", name)
    fileInfo.description !== description && formData.append("description", description)
    fileInfo?.filetype?.name !== filetype && formData.append("filetype", filetype.name)
    formData.append("metadata", JSON.stringify({ ...extraMetaData, ...reqMetaData }))
    if (singleFileType) {
      // Process favourite upload right now in the Django server rather than queueing - faster and no worries regarding
      // user experience of the file not being processed by the time the page refreshes
      formData.append("process_immediately", true)
    }

    const url = creatingFile
      ? `/lake/files/direct_upload/`
      : `/lake/files/${fileInfo.id}/update_and_reprocess/`
    const uploadMethod = creatingFile ? addNewObject : updateObject
    const successMsg = getText(
      `administration.successful-file-${creatingFile ? "uploaded" : "update"}`,
      `File was ${creatingFile ? "uploaded" : "updated"} successfully!`,
    )

    uploadMethod(url, formData, true, true, false)
      .then((_) => {
        alert(successMsg)
        if (!singleFileType) {
          tableReload()
        }
        onHide()
      })
      .catch((error) => {
        error.text().then((error) => {
          let err
          try {
            err = JSON.parse(error)["message"]
            if (err.includes("duplicate key value violates unique constraint")) {
              err = getText(
                "favourites.validation.name.uniqueness",
                "Each favourite must have a unique name. Please choose a different one.",
              )
            }
          } catch (e) {
            try {
              err = JSON.parse(error)["detail"]
            } catch (e) {
              err = "Unknown problem"
            }
          }
          setFileSubmitErr(`${getText("general.server.error", "Error! Could not process request.")}: ${err}`)
          console.error(err)
          logToServer(err)
        })
      })
  }

  const handleFileUpload = (e) => {
    e.preventDefault()
    if (e.target.files.length > 0) {
      setFileSubmitErr(null)
      setFile(e.target.files[0])
    }
  }

  return (
    <Modal
      size={"lg"}
      dialogClassName={"common-modal"}
      show={show}
      onHide={onHide}
      style={{ overflowX: "scroll", overflowY: "scroll" }}
      backdrop={"static"}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {getText(
            `administration.${creatingFile ? "upload" : "edit"}-file`,
            `${creatingFile ? "Upload" : "Edit"} File`,
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div id="form_edit_file">
          {error ? (
            <>
              <p className={"error-message"}>
                {getText("general.server.error", "Error! Could not process request.")}
              </p>
              {fileErr && <p className={"error-message"}>{fileErr}</p>}
              {filetypeErr && <p className={"error-message"}>{filetypeErr}</p>}
            </>
          ) : (
            <>
              {fileInfo ? (
                <>
                  <InputElement
                    getText={getText}
                    label_id={"general.name"}
                    value={name}
                    onUpdate={(val) => {
                      setFileSubmitErr(null)
                      setName(val)
                    }}
                    disabled={false}
                  />
                  <InputElement
                    getText={getText}
                    label_id={"general.description"}
                    value={description}
                    onUpdate={(val) => {
                      setFileSubmitErr(null)
                      setDescription(val)
                    }}
                    disabled={false}
                  />
                  <>
                    {!singleFileType && (
                      <div style={{ marginBottom: "10px" }}>
                        <label>{getText("general.type", "Type")}</label>: &nbsp;
                        {!filetype?.name && <RequiredSpan getText={getText} />}
                        <DropdownButton
                          id={"filetype-select"}
                          variant={"outline-primary"}
                          title={
                            filetype?.name ||
                            `-- ${getText("general.dropdown.choose.type", "Choose Type")} --`
                          }
                        >
                          {fileTypeOptions.map((item, i) => (
                            <Dropdown.Item
                              key={`Item${i}`}
                              onClick={() => {
                                setFileSubmitErr(null)
                                getFileTypeInfo(item, extraMetaData)
                              }}
                            >
                              {item.name}
                            </Dropdown.Item>
                          ))}
                        </DropdownButton>
                      </div>
                    )}
                    {/* Get required file metadata */}
                    {reqMetaData &&
                      Object.entries(reqMetaData).map(([key, value], i) => {
                        return (
                          <React.Fragment key={`metadata_${i}`}>
                            <InputElement
                              getText={getText}
                              label_id={`general.${key}`}
                              value={value}
                              onUpdate={(new_val) => {
                                setReqMetaData((prevState) => ({
                                  ...prevState,
                                  [key]: new_val,
                                }))
                                setFileSubmitErr(null)
                              }}
                              disabled={false}
                            />
                          </React.Fragment>
                        )
                      })}
                    {Object.keys(extraMetaData || {}).length > 0 && (
                      <>
                        <label>{getText("general.original.metadata", "Original Metadata")}</label>
                        :
                        <textarea
                          className={"input-full-width"}
                          name={"extra_metadata"}
                          disabled={true}
                          value={JSON.stringify(extraMetaData)}
                          onChange={() => {}}
                        />
                      </>
                    )}
                  </>
                  <div style={{ marginBottom: "35px" }}>
                    <label>{getText("general.file", "File")}</label>:
                    {creatingFile && !file && <RequiredSpan getText={getText} />}
                    <div>
                      <input
                        type={"file"}
                        style={{ margin: "auto" }}
                        multiple={false}
                        onChange={handleFileUpload}
                      />
                    </div>
                  </div>
                  {fileSubmitErr && (
                    <div>
                      <p className={"error-message"}>{fileSubmitErr}</p>
                    </div>
                  )}
                </>
              ) : (
                <DashLoader />
              )}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant={"outline-primary"} onClick={onHide}>
          {getText("general.closeButton", "Close")}
        </Button>
        <Button variant={"primary"} onClick={submitFileEdit} disabled={error}>
          {getText("general.submitButton", "Submit")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

const InputElement = ({ getText, label_id, value, onUpdate, disabled = true }) => {
  const field_dict = {
    "general.name": "Name",
    "general.description": "Description",
  }
  return (
    <div style={{ marginBottom: "10px" }}>
      <label>
        <span>{getText(label_id, field_dict[label_id])}</span>: &nbsp;
        {value === "" && <RequiredSpan getText={getText} />}
      </label>
      <input
        type={"text"}
        value={value}
        onChange={(e) => {
          e.preventDefault()
          onUpdate(e.target.value)
        }}
        style={{ width: "100%", padding: "5px", border: "solid var(--lanzalake-grey) 1px" }}
        disabled={disabled}
      />
    </div>
  )
}

const RequiredSpan = ({ getText }) => {
  return (
    <span className={"warning-message"}>
      {getText("general.warning.requiredFormField", "* Required Field")}
    </span>
  )
}
